import React, { useState } from 'react';
import { Mail, User, MessageSquare, Send, CheckCircle, Phone, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Ornament from './Ornament';
import { businessInfo } from '../shopData';

const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const buildWhatsAppMessage = () => {
    const lines = [
      "Hi Shri Kothari Jewellers,",
      "",
      "I'd like to get in touch.",
      "",
      `Name: ${formData.name}`,
      `Phone: ${formData.phone}`,
      `Email: ${formData.email}`,
      "",
      "Message:",
      formData.message,
    ];
    return lines.join("\n");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const waUrl = `https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(buildWhatsAppMessage())}`;

    const waTab = window.open('about:blank', '_blank');

    toast.success('Opening WhatsApp...');
    setIsSubmitted(true);

    if (waTab && !waTab.closed) {
      waTab.location = waUrl;
    } else {
      window.location.href = waUrl;
    }

    setIsSubmitting(false);

    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitted(false);
    }, 4000);
  };

  return (
    <section id="contact" className="section-padding bg-dark">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="lux-eyebrow">Get in Touch</div>
          <h2 className="lux-title">Visit Us or Send a Message</h2>
          <Ornament />
          <p className="lux-subtitle">
            Step into our store in the heart of Indore, or reach us instantly &mdash; we'd love to help you find the perfect piece.
          </p>
        </div>

        <div className="contact-quick-grid">
          <a href={`tel:${businessInfo.phone}`} className="contact-quick-card" data-testid="contact-quick-call">
            <div className="contact-quick-icon"><Phone size={22} /></div>
            <div className="contact-quick-label">Call Us</div>
            <div className="contact-quick-value">{businessInfo.phone}</div>
          </a>
          <a
            href={`https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent("Hi, I have a question about your silver collection.")}`}
            target="_blank" rel="noopener noreferrer"
            className="contact-quick-card" data-testid="contact-quick-whatsapp"
          >
            <div className="contact-quick-icon"><WhatsAppIcon size={22} /></div>
            <div className="contact-quick-label">Chat on WhatsApp</div>
            <div className="contact-quick-value">Quickest response</div>
          </a>
          <a href={businessInfo.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="contact-quick-card" data-testid="contact-quick-visit">
            <div className="contact-quick-icon"><MapPin size={22} /></div>
            <div className="contact-quick-label">Visit Store</div>
            <div className="contact-quick-value">Bada Sarafa, Indore</div>
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-8 items-start">
          <div className="contact-form-wrapper-lux contact-form-compact">
            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form-subheading">
                  <span>Send a Message</span>
                  <span className="contact-form-subheading-hint"><Clock size={13} /> We reply within 24 hours</span>
                </div>
                <div className="contact-form-row">
                  <div className="form-group">
                    <label className="form-label"><User size={14} /><span>Name</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="form-input" placeholder="Your name" data-testid="contact-form-name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label"><Phone size={14} /><span>Phone</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="form-input" placeholder="+91 XXXXX XXXXX" data-testid="contact-form-phone" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label"><Mail size={14} /><span>Email</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="form-input" placeholder="you@example.com" data-testid="contact-form-email" />
                </div>
                <div className="form-group">
                  <label className="form-label"><MessageSquare size={14} /><span>Message</span></label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows="3" className="form-input" placeholder="Tell us what you're looking for..." data-testid="contact-form-message"></textarea>
                </div>
                <button type="submit" className="btn-gold-large w-full" disabled={isSubmitting} data-testid="contact-form-submit">
                  <Send size={16} />
                  <span>{isSubmitting ? 'Opening WhatsApp...' : 'Send Message via WhatsApp'}</span>
                </button>
              </form>
            ) : (
              <div className="success-message" style={{ textAlign: 'center', padding: '1rem 0' }}>
                <CheckCircle size={56} className="text-gold-primary mb-4" style={{ margin: '0 auto 1rem' }} />
                <h3 className="text-2xl font-bold text-white mb-2">WhatsApp Opened!</h3>
                <p className="text-gray-300 mb-4">Just hit Send in WhatsApp to reach us. If it didn't open, use the button below.</p>
                <a
                  href={`https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(buildWhatsAppMessage())}`}
                  target="_blank" rel="noopener noreferrer"
                  className="lux-btn-outline" data-testid="contact-form-success-wa"
                  style={{ marginTop: '0.5rem', display: 'inline-flex' }}
                >
                  <WhatsAppIcon size={18} /><span>Open WhatsApp Manually</span>
                </a>
              </div>
            )}
          </div>

          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3680.318!2d75.850008!3d22.718065!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fda9fadfc023%3A0xcca0f768abc3df72!2sShri%20Kothari%20Jewellers!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
              width="100%" height="100%"
              style={{ border: 0, minHeight: '420px' }}
              allowFullScreen="" loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Store Location" className="map-iframe"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
